/**
 * Two presentations share this file:
 *  .tile — the prototype's `.stat`: a plain hero number used inside grids.
 *  .kpi  — the prototype's `.kpi`: the Command Center's headline card, with a
 *          tinted icon chip, a radial corner glow and a hover lift.
 * `--kpc` carries the accent so the glow, icon chip and border all derive from
 * one value rather than repeating a colour in three places.
 */
.tile {
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
  padding: var(--sp-6);
  border: 1px solid var(--line);
  border-radius: var(--radius-lg);
  background: linear-gradient(150deg, var(--surf2), var(--surf));
  position: relative;
  overflow: hidden;
}
.tile::after {
  content: '';
  position: absolute;
  top: -30px;
  right: -30px;
  width: 90px;
  height: 90px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    color-mix(in srgb, var(--kpc, var(--magenta)) 14%, transparent),
    transparent 70%
  );
}
.head {
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  position: relative;
  z-index: 1;
}
.icon {
  font-size: 15px;
  line-height: 1;
}
.label {
  margin-top: 5px;
  color: var(--sage);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.6px;
  line-height: 1.4;
  text-transform: uppercase;
}
.value {
  font-family: var(--font-display);
  font-size: 30px;
  font-weight: 700;
  line-height: 1;
  color: var(--text);
  position: relative;
  z-index: 1;
}
.caption {
  color: var(--sage);
  font-size: 10px;
  font-weight: 600;
  position: relative;
  z-index: 1;
}

/* ── KPI variant ─────────────────────────────────────────────────────────── */
.kpi {
  padding: 15px 16px 13px;
  border-radius: 14px;
  background: linear-gradient(155deg, var(--surf2), var(--surf));
  cursor: default;
  transition:
    transform 0.18s,
    border-color 0.18s,
    box-shadow 0.18s;
}
.kpi::after {
  top: -34px;
  right: -34px;
  width: 96px;
  height: 96px;
  background: radial-gradient(
    circle,
    color-mix(in srgb, var(--kpc, var(--magenta)) 22%, transparent),
    transparent 70%
  );
}
.kpi:hover {
  transform: translateY(-3px);
  border-color: var(--kpc, var(--magenta));
  box-shadow: 0 10px 26px rgb(0 0 0 / 32%);
}
.kpi .head {
  gap: 9px;
  margin-bottom: 11px;
}
.kpi .iconChip {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border-radius: 9px;
  background: color-mix(in srgb, var(--kpc, var(--magenta)) 18%, transparent);
  color: var(--kpc, var(--magenta));
  font-size: 15px;
  line-height: 1;
}
.kpi .label {
  margin-top: 0;
  font-weight: 700;
  letter-spacing: 0.5px;
  line-height: 1.25;
}
.kpi .value {
  font-size: 38px;
}
.kpi .caption {
  margin-top: 9px;
  font-size: 10.5px;
  font-weight: 700;
}

@media (prefers-reduced-motion: reduce) {
  .kpi:hover {
    transform: none;
  }
}
